/**
 * TanStack Query hooks for taxi plans (the waypoint editor's persistence).
 * Mirrors the obstacle hooks: list/get queries + a mutations hook that pulls the
 * active org from `useTenant`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  createTaxiPlan,
  deleteTaxiPlan,
  duplicateTaxiPlan,
  getTaxiPlan,
  listTaxiPlans,
  updateTaxiPlan,
} from '@/lib/api/taxiPlans'
import type { TaxiPlan } from '@/demo/masters-planner/data/taxiGraph'

export const taxiPlanKeys = {
  all: ['taxi-plans'] as const,
  list: (locationId: string) => [...taxiPlanKeys.all, 'list', locationId] as const,
  detail: (id: string) => [...taxiPlanKeys.all, 'detail', id] as const,
}

export function useTaxiPlans(locationId: string | undefined) {
  return useQuery({
    queryKey: taxiPlanKeys.list(locationId ?? ''),
    queryFn: () => listTaxiPlans(locationId!),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

export function useTaxiPlan(id: string | undefined) {
  return useQuery({
    queryKey: taxiPlanKeys.detail(id ?? ''),
    queryFn: () => getTaxiPlan(id!),
    enabled: !!id,
  })
}

export function useTaxiPlanMutations() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  const invalidate = (locationId: string) => {
    queryClient.invalidateQueries({ queryKey: taxiPlanKeys.list(locationId) })
  }

  /** Save a plan: insert when it has no DB id yet, else update. */
  const save = useMutation({
    mutationFn: async (plan: TaxiPlan): Promise<TaxiPlan> => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      // A persisted plan id is a DB uuid; freshly-seeded plans use a `plan_…` id.
      const isPersisted = !plan.id.startsWith('plan_')
      return isPersisted
        ? updateTaxiPlan(plan.id, plan)
        : createTaxiPlan(currentOrganization.id, plan)
    },
    onSuccess: (saved) => invalidate(saved.locationId),
  })

  const duplicate = useMutation({
    mutationFn: async ({ source, name }: { source: TaxiPlan; name: string }): Promise<TaxiPlan> => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return duplicateTaxiPlan(currentOrganization.id, source, name)
    },
    onSuccess: (saved) => invalidate(saved.locationId),
  })

  const remove = useMutation({
    mutationFn: async ({ id }: { id: string; locationId: string }) => deleteTaxiPlan(id),
    onSuccess: (_d, vars) => invalidate(vars.locationId),
  })

  return { save, duplicate, remove }
}
